import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EpcContractHandoffPanel } from '@/components/epc/commercial-ux/EpcContractHandoffPanel';
import { buildContractReviewContext } from '@/components/epc/commercial-ux/awardReconciliationUx';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatMoney } from '@/utils/format';

const { Title, Text } = Typography;

const EpcContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusId, setFocusId] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['epc-contracts'],
    queryFn: () => epcCommercialService.listContracts(),
    staleTime: 15_000,
  });
  const rows = (listQ.data || []) as Record<string, unknown>[];

  const focusRow = useMemo(() => {
    if (!focusId) return null;
    return rows.find((r) => String(r.id) === focusId) || null;
  }, [rows, focusId]);

  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus) setFocusId(focus);
  }, [searchParams]);

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <Title level={3} style={{ marginBottom: 4 }}>
          EPC Contracts
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/epc/commercial/contracts/new')}
        >
          Register contract
        </Button>
      </Space>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Draft → Submit → Approve → Activate. Contracts come from an accepted award, or are registered
        directly when signed outside tender / estimation. Complete particulars in the contract
        cockpit, then open CA workspace for post-award administration.
      </Text>
      <Card>
        <Table
          loading={listQ.isLoading}
          rowKey={(r) => String(r.id)}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
          onRow={(r) => ({
            onClick: () => setFocusId(String(r.id)),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'Number',
              dataIndex: 'contract_number',
              width: 140,
              render: (v, r) => (
                <Link
                  to={`/epc/commercial/contracts/${r.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {v ? String(v) : '—'}
                </Link>
              ),
            },
            { title: 'Title', dataIndex: 'title' },
            {
              title: 'Origin',
              key: 'origin',
              width: 110,
              render: (_: unknown, r) =>
                String(r.origin || '') === 'DIRECT' || !r.award_id ? (
                  <Tag color="blue">Direct</Tag>
                ) : (
                  <Tag>Award</Tag>
                ),
            },
            {
              title: 'Client ref',
              dataIndex: 'client_contract_ref',
              width: 140,
              render: (v) => (v ? String(v) : '—'),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (s: string) => <Tag>{s}</Tag>,
            },
            {
              title: 'Contract Review',
              key: 'cr',
              width: 140,
              render: (_: unknown, r) => {
                const cr = buildContractReviewContext(r);
                if (!cr.linked || !cr.path) return '—';
                return (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(cr.path!);
                    }}
                  >
                    Open CR
                  </Button>
                );
              },
            },
            {
              title: 'Value',
              dataIndex: 'contract_value',
              width: 120,
              render: (v, r) =>
                v != null
                  ? formatMoney(Number(v), { currency: String(r.currency || 'AED') })
                  : '—',
            },
            {
              title: 'CA',
              key: 'ca',
              width: 100,
              render: (_: unknown, r) => (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/epc/commercial/contracts/${r.id}`);
                  }}
                >
                  Open
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        title="EPC Contract cockpit"
        open={Boolean(focusRow)}
        onClose={() => {
          setFocusId(null);
          if (searchParams.has('focus')) {
            const next = new URLSearchParams(searchParams);
            next.delete('focus');
            setSearchParams(next, { replace: true });
          }
        }}
        width={Math.min(960, typeof window !== 'undefined' ? window.innerWidth - 24 : 960)}
        styles={{ body: { paddingBottom: 24 } }}
        destroyOnClose
      >
        {focusRow ? (
          <EpcContractHandoffPanel contract={focusRow} onUpdated={() => listQ.refetch()} />
        ) : null}
      </Drawer>
    </div>
  );
};

export default EpcContractsPage;
